import type { ComponentType } from "react"
import { useCallback } from "react"
import { loadStripe } from "@stripe/stripe-js"
import { Link } from "@tanstack/react-router"
import { IconCircleArrowUpOutline18 } from "nucleo-ui-outline-18"

import type { PaidPlan, Plan } from "@productlane/lib/src/browser"
import { PLAN_DISPLAY_NAMES } from "@productlane/lib/src/browser"
import { Alert, Button } from "@productlane/ui"

import { toast } from "@/components/toast"
import { env } from "@/env"
import { api } from "@/lib/api"
import { getPlanGateAction } from "@/lib/plan-gate"
import { useRequiredPlans } from "@/lib/plans"
import { useTrial } from "@/lib/useTrial"

interface IntegrationPlanBannerProps {
	feature: string
	requiredPlans: Plan[]
	description: string
	icon?: ComponentType<{ className?: string }>
}

export function IntegrationPlanBanner({
	feature,
	requiredPlans,
	description,
	icon,
}: IntegrationPlanBannerProps) {
	const { passes: hasRequiredPlan, initialized } =
		useRequiredPlans(requiredPlans)
	const { isTrial, hadRecentCancellation, billingInterval } = useTrial()
	const requiredPlan = requiredPlans[0] as PaidPlan
	const planName = PLAN_DISPLAY_NAMES[requiredPlan]

	const trialMutation = api.workspace.createTrialSubscription.useMutation({
		onError: (error) => {
			toast.error({
				title: `The ${planName} trial could not be started`,
				description: error.message,
				icon,
			})
		},
	})

	const startTrial = useCallback(async () => {
		const result = await trialMutation.mutateAsync({
			plan: requiredPlan,
			interval: billingInterval,
		})
		if (!result) {
			return
		}
		if (result.startsWith("http")) {
			location.href = result
			return
		}
		const stripe = await loadStripe(env.VITE_STRIPE_PUBLISHABLE_KEY)
		await stripe?.redirectToCheckout({ sessionId: result })
	}, [billingInterval, requiredPlan, trialMutation])

	const action = getPlanGateAction({
		hasRequiredPlan,
		initialized,
		isTrial,
		hadRecentCancellation,
	})

	if (action === "none") {
		return null
	}

	return (
		<Alert
			header={`${feature} is part of the ${planName} plan`}
			variant="default"
			icon={icon}
			className="mb-8 select-none"
		>
			<div className="mt-1 flex flex-col gap-3">
				<div>{description}</div>
				<div className="flex items-center gap-2">
					{action === "start-trial" && (
						<Button
							Icon={IconCircleArrowUpOutline18}
							isLoading={trialMutation.isLoading}
							onClick={() => void startTrial()}
						>
							Start 7-day trial
						</Button>
					)}
					<Link to="/settings/plans">
						<Button variant="outline">View plans</Button>
					</Link>
				</div>
			</div>
		</Alert>
	)
}
